/**
 * 管理后台 · 用户与等级管理。
 * 替代老系统 UserinfoAction / UserEditAction / RightEditAction。
 */

import { useCallback, useEffect, useState } from 'react'

import { adminApi, formatTime, type GradeRow, type UserRow } from '../../lib/adminApi'
import { formatPerm } from '../../lib/protocol'
import { PermEditor } from './PermEditor'
import { Pager } from './RoomsPanel'

interface Props {
  token: string
  onError: (msg: string) => void
  onNotice: (msg: string) => void
}

export function UsersPanel({ token, onError, onNotice }: Props) {
  const [rows, setRows] = useState<UserRow[]>([])
  const [grades, setGrades] = useState<GradeRow[]>([])
  const [total, setTotal] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<UserRow | null>(null)
  const [creating, setCreating] = useState(false)
  const limit = 20

  const load = useCallback(async () => {
    try {
      const [p, g] = await Promise.all([
        adminApi.listUsers(token, keyword, limit, offset),
        adminApi.listGrades(token),
      ])
      setRows(p.items ?? [])
      setTotal(p.total)
      setGrades(g.grades ?? [])
    } catch (e) {
      onError(e instanceof Error ? e.message : '加载失败')
    }
  }, [token, keyword, offset, onError])

  useEffect(() => {
    void load()
  }, [load])

  const toggleLock = async (u: UserRow) => {
    try {
      await adminApi.updateUser(token, u.account, { locked: !u.locked })
      onNotice(u.locked ? '已解锁' : '已锁定')
      void load()
    } catch (e) {
      onError(e instanceof Error ? e.message : '操作失败')
    }
  }

  const remove = async (u: UserRow) => {
    if (!confirm(`确定删除账号「${u.account}」吗？\n该用户的历史日志会保留。`)) return
    try {
      await adminApi.deleteUser(token, u.account)
      onNotice('账号已删除')
      void load()
    } catch (e) {
      onError(e instanceof Error ? e.message : '删除失败')
    }
  }

  return (
    <div className="adm-panel">
      <div className="adm-bar">
        <input
          placeholder="搜索账号 / 昵称 / 邮箱"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value)
            setOffset(0)
          }}
        />
        <button className="primary" onClick={() => setCreating(true)}>
          新建账号
        </button>
        <span className="adm-count">共 {total} 个账号</span>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>账号</th>
              <th>昵称</th>
              <th>等级</th>
              <th>邮箱</th>
              <th>注册时间</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.account} className={u.locked ? 'locked' : ''}>
                <td className="mono">{u.account}</td>
                <td>{u.nick || '—'}</td>
                <td>{u.grade || '—'}</td>
                <td className="dim">{u.email || '—'}</td>
                <td className="dim">{formatTime(u.regTime)}</td>
                <td>
                  {u.locked ? (
                    <em className="tag tag-warn">已锁定</em>
                  ) : (
                    <em className="tag tag-ok">正常</em>
                  )}
                </td>
                <td className="adm-ops">
                  <button className="mini" onClick={() => setEditing(u)}>
                    编辑
                  </button>
                  <button className="mini" onClick={() => void toggleLock(u)}>
                    {u.locked ? '解锁' : '锁定'}
                  </button>
                  <button className="mini danger" onClick={() => void remove(u)}>
                    删除
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="adm-empty">
                  没有匹配的账号
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager total={total} limit={limit} offset={offset} onChange={setOffset} />

      <GradesSection token={token} grades={grades} onError={onError} onSaved={() => void load()} />

      {(editing || creating) && (
        <UserDialog
          token={token}
          user={editing}
          grades={grades}
          onClose={() => {
            setEditing(null)
            setCreating(false)
          }}
          onSaved={() => {
            setEditing(null)
            setCreating(false)
            onNotice('已保存')
            void load()
          }}
          onError={onError}
        />
      )}
    </div>
  )
}

/** 等级与默认权限。 */
function GradesSection({
  token,
  grades,
  onError,
  onSaved,
}: {
  token: string
  grades: GradeRow[]
  onError: (m: string) => void
  onSaved: () => void
}) {
  const [editing, setEditing] = useState<GradeRow | null>(null)

  return (
    <div className="adm-section">
      <h3>等级与默认权限</h3>
      <p className="adm-hint">
        账号的最终权限 = 等级权限 ∪ 房间默认权限。这与老系统的算法一致。
      </p>
      <div className="grade-list">
        {grades.map((g) => (
          <button key={g.name} className="grade-card" onClick={() => setEditing(g)}>
            <strong>{g.name}</strong>
            <span className="dim">等级 {g.level}</span>
            <code>{formatPerm(g.prop) || '（无字母表示）'}</code>
          </button>
        ))}
        <button
          className="grade-card new"
          onClick={() => setEditing({ name: '', prop: 0, level: 0 })}
        >
          + 新增等级
        </button>
      </div>

      {editing && (
        <GradeDialog
          token={token}
          grade={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onSaved()
          }}
          onError={onError}
        />
      )}
    </div>
  )
}

function GradeDialog({
  token,
  grade,
  onClose,
  onSaved,
  onError,
}: {
  token: string
  grade: GradeRow
  onClose: () => void
  onSaved: () => void
  onError: (m: string) => void
}) {
  const [name, setName] = useState(grade.name)
  const [level, setLevel] = useState(String(grade.level))
  const [prop, setProp] = useState(grade.prop)

  const save = async () => {
    if (!name.trim()) {
      onError('等级名不能为空')
      return
    }
    try {
      await adminApi.upsertGrade(token, {
        name: name.trim(),
        prop,
        level: Number(level) || 0,
      })
      onSaved()
    } catch (e) {
      onError(e instanceof Error ? e.message : '保存失败')
    }
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg" onClick={(e) => e.stopPropagation()}>
        <h3>{grade.name ? `编辑等级「${grade.name}」` : '新增等级'}</h3>
        <div className="dlg-grid">
          <label>
            <span>等级名称</span>
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={!!grade.name} />
          </label>
          <label>
            <span>等级序号</span>
            <input value={level} onChange={(e) => setLevel(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        <div className="dlg-section">
          <h4>该等级的默认权限</h4>
          <PermEditor value={prop} onChange={setProp} />
        </div>
        <div className="dlg-foot">
          <button onClick={onClose}>取消</button>
          <button className="primary" onClick={() => void save()}>
            保存
          </button>
        </div>
      </div>
    </div>
  )
}

/** 用户新建/编辑对话框。 */
function UserDialog({
  token,
  user,
  grades,
  onClose,
  onSaved,
  onError,
}: {
  token: string
  user: UserRow | null
  grades: GradeRow[]
  onClose: () => void
  onSaved: () => void
  onError: (m: string) => void
}) {
  const isNew = !user
  const [account, setAccount] = useState(user?.account ?? '')
  const [nick, setNick] = useState(user?.nick ?? '')
  const [grade, setGrade] = useState(user?.grade ?? grades[0]?.name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      if (isNew) {
        if (!account.trim() || !password) {
          onError('账号与密码不能为空')
          return
        }
        await adminApi.createUser(token, { account: account.trim(), nick, grade, email, password })
      } else {
        await adminApi.updateUser(token, user!.account, {
          nick,
          grade,
          email,
          // 留空表示不改密码
          ...(password ? { password } : {}),
        })
      }
      onSaved()
    } catch (e) {
      onError(e instanceof Error ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg narrow" onClick={(e) => e.stopPropagation()}>
        <h3>{isNew ? '新建账号' : `编辑账号 ${user!.account}`}</h3>
        <div className="dlg-grid">
          <label>
            <span>账号</span>
            <input value={account} onChange={(e) => setAccount(e.target.value)} disabled={!isNew} />
          </label>
          <label>
            <span>昵称</span>
            <input value={nick} onChange={(e) => setNick(e.target.value)} />
          </label>
          <label>
            <span>等级</span>
            <select value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">（未设置）</option>
              {grades.map((g) => (
                <option key={g.name} value={g.name}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>邮箱</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="span2">
            <span>{isNew ? '密码' : '重置密码'}</span>
            <input
              type="password"
              value={password}
              placeholder={isNew ? '' : '留空表示不修改'}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        </div>
        <div className="dlg-foot">
          <button onClick={onClose}>取消</button>
          <button className="primary" disabled={busy} onClick={() => void save()}>
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
